<template>
  <el-dialog
    :model-value="modelValue"
    :title="dialogTitle"
    width="min(400px, 92%)"
    align-center
    destroy-on-close
    append-to-body
    modal-class="major-catalog-login-modal"
    class="major-catalog-login-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
    @closed="onDialogClosed"
  >
    <div class="mcld-tabs" role="tablist">
      <button
        type="button"
        class="mcld-tab"
        :class="{ 'mcld-tab--active': loginMode === 'account' }"
        @click="loginMode = 'account'"
      >
        账号登录
      </button>
      <button
        type="button"
        class="mcld-tab"
        :class="{ 'mcld-tab--active': loginMode === 'ding' }"
        @click="loginMode = 'ding'"
      >
        钉钉登录
      </button>
    </div>

    <div v-show="loginMode === 'account'" class="mcld-body">
      <el-form ref="ruleFormRef" :model="loginForm" :rules="rules" label-width="88px" class="mcld-form">
        <el-form-item label="账号" prop="account">
          <el-input v-model="loginForm.account" placeholder="请输入账号" clearable />
        </el-form-item>
        <el-form-item label="密码" prop="password">
          <el-input v-model="loginForm.password" type="password" placeholder="请输入密码" show-password />
        </el-form-item>
        <el-form-item label="验证码" prop="imageValue">
          <el-input
            v-model="loginForm.imageValue"
            placeholder="请输入图片验证码"
            @keyup.enter="submitAccount"
          >
            <template #suffix>
              <el-image
                class="mcld-captcha-img"
                :src="loginForm.image"
                fit="cover"
                @click="refreshCaptcha"
              />
            </template>
          </el-input>
        </el-form-item>
        <div class="mcld-actions">
          <el-button type="primary" :loading="accountLoading" @click="submitAccount">登 录</el-button>
        </div>
      </el-form>
    </div>

    <div v-show="loginMode === 'ding'" class="mcld-body mcld-ding">
      <p class="mcld-ding-lead">已绑定钉钉账号的成员，可在钉钉内一键进入系统</p>
      <el-button class="mcld-ding-btn" type="primary" :loading="dingLoading" @click="dingLogin">
        钉钉一键登录
      </el-button>
      <p class="mcld-ding-tip">
        <span>请在钉钉客户端内打开本页面后再使用钉钉登录</span>
      </p>
    </div>
  </el-dialog>
</template>

<script>
import Cookies from 'js-cookie'
import { showNotify } from 'vant'
import { hex_md5 } from '@/common/js/hexMd5'

/** 与 index / courseMajorDisplay 一致：走 https.js 的 axios（window.baseURL），避免 fetch 绝对地址跨域 */
const API_CAPTCHA = '/base/getSysLoginVerificationCodeImage'
const API_LOGIN = '/login'
const API_CORP_ID = '/getCorpId'
const API_DING_LOGIN = '/codeAuthLogin'

export default {
  name: 'MajorCatalogLoginDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    /** 标题：列表页进入用默认文案；token 失效等场景可由父组件传入 */
    dialogTitle: {
      type: String,
      default: '登录后继续查看专业详情',
    },
  },
  emits: ['update:modelValue', 'success'],
  data() {
    return {
      loginMode: 'account',
      accountLoading: false,
      dingLoading: false,
      loginForm: {
        account: '',
        password: '',
        imageValue: '',
        image: '',
      },
      rules: {
        account: [{ required: true, message: '请输入账号', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }],
        imageValue: [{ required: true, message: '请输入图片验证码', trigger: 'blur' }],
      },
    }
  },
  watch: {
    modelValue(v) {
      if (v) {
        this.loginMode = 'account'
        this.$nextTick(() => this.refreshCaptcha())
      }
    },
  },
  methods: {
    onDialogClosed() {
      this.accountLoading = false
      this.dingLoading = false
      this.loginForm.imageValue = ''
    },
    refreshCaptcha() {
      this.$http
        .post(API_CAPTCHA, {})
        .then((data) => {
          if (data.resultCode === 0 || data.resultCode === '0') {
            this.loginForm.image = data.resultData
          } else {
            showNotify(data.resultMsg || '获取验证码失败')
          }
        })
        .catch(() => {
          showNotify('获取验证码失败')
        })
    },
    submitAccount() {
      this.$refs.ruleFormRef.validate((valid) => {
        if (!valid) return
        this.accountLoading = true
        const paramFrom = {
          account: this.loginForm.account,
          password: hex_md5(this.loginForm.imageValue) + hex_md5(this.loginForm.password),
          imageValue: this.loginForm.imageValue,
        }
        this.$http
          .post(API_LOGIN, paramFrom)
          .then((data) => {
            const rc = data.resultCode
            if (rc === 0 || rc === '0') {
              this.applyLoginResult(data.resultData)
              showNotify({ type: 'success', message: data.resultMsg || '登录成功' })
            } else {
              showNotify(data.resultMsg || '登录失败')
              this.refreshCaptcha()
            }
          })
          .catch(() => {
            showNotify('登录请求失败')
            this.refreshCaptcha()
          })
          .finally(() => {
            this.accountLoading = false
          })
      })
    },
    applyLoginResult(resultData) {
      if (!resultData || !resultData.access_token) {
        showNotify('登录返回数据异常')
        return
      }
      const token = resultData.access_token
      const exp = { expires: 1, path: '/' }
      Cookies.set('access_token', token, exp)
      Cookies.set('sys_access_token', token, exp)
      try {
        localStorage.setItem('sys_userInfo', JSON.stringify(resultData.userInfo || {}))
      } catch (e) {
        /* ignore */
      }
      this.$emit('success')
      this.$emit('update:modelValue', false)
    },
    loadDingTalkScript(callback) {
      if (typeof window.dd !== 'undefined' && window.dd.env) {
        callback()
        return
      }
      const s = document.createElement('script')
      s.src = 'https://g.alicdn.com/dingding/dingtalk-jsapi/2.13.42/dingtalk.open.js'
      s.onload = () => callback()
      s.onerror = () => {
        this.dingLoading = false
        showNotify('钉钉 SDK 加载失败')
      }
      document.head.appendChild(s)
    },
    dingLogin() {
      this.dingLoading = true
      this.loadDingTalkScript(() => {
        this.$http
          .post(API_CORP_ID, {})
          .then((data) => {
            if (data.resultCode !== 0 && data.resultCode !== '0') {
              this.dingLoading = false
              showNotify(data.resultMsg || '获取企业信息失败')
              return
            }
            const corpId = data.resultData && data.resultData.CorpId
            const dd = window.dd
            if (!dd || !dd.env) {
              this.dingLoading = false
              showNotify('钉钉环境异常')
              return
            }
            if (dd.env.platform === 'notInDingTalk') {
              this.dingLoading = false
              showNotify('请在钉钉客户端内打开本页面后再使用钉钉登录')
              return
            }
            dd.ready(() => {
              dd.runtime.permission.requestAuthCode({
                corpId,
                onSuccess: (info) => {
                  this.$http
                    .post(API_DING_LOGIN, { dd_code: info.code })
                    .then((res) => {
                      this.dingLoading = false
                      const rc = res.resultCode
                      if (rc === 0 || rc === '0') {
                        this.applyLoginResult(res.resultData)
                        showNotify({ type: 'success', message: res.resultMsg || '登录成功' })
                      } else {
                        showNotify(res.resultMsg || '钉钉登录失败')
                      }
                    })
                    .catch(() => {
                      this.dingLoading = false
                      showNotify('钉钉登录请求失败')
                    })
                },
                onFail: (err) => {
                  this.dingLoading = false
                  const msg = err && err.errorMessage ? err.errorMessage : JSON.stringify(err || {})
                  showNotify('钉钉授权失败：' + msg)
                },
              })
            })
          })
          .catch(() => {
            this.dingLoading = false
            showNotify('获取企业信息失败')
          })
      })
    },
  },
}
</script>

<style scoped>
/* 勿改 el-dialog 自身 flex，交给 align-center；仅限制正文高度防溢出 */
.major-catalog-login-dialog :deep(.el-dialog__body) {
  padding-top: 8px;
  max-height: min(58vh, 420px);
  overflow-y: auto;
  box-sizing: border-box;
}

.mcld-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 18px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #dcdfe6;
}

.mcld-tab {
  flex: 1;
  padding: 10px 12px;
  border: none;
  background: #f5f7fa;
  font-size: 14px;
  cursor: pointer;
  color: #606266;
}

.mcld-tab--active {
  background: #175cc4;
  color: #fff;
  font-weight: 600;
}

.mcld-form {
  max-width: 100%;
}

.mcld-captcha-img {
  width: 120px;
  height: 36px;
  cursor: pointer;
  border-radius: 4px;
  vertical-align: middle;
}

.mcld-actions {
  text-align: center;
  margin-top: 8px;
}

.mcld-ding {
  text-align: center;
  padding: 8px 0 4px;
}

.mcld-ding-lead {
  margin: 0 0 16px;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
}

.mcld-ding-btn {
  min-width: 200px;
}

.mcld-ding-tip {
  margin: 14px 0 0;
  font-size: 12px;
  color: #909399;
}
</style>

<!-- append-to-body 后弹窗在 body 下，需非 scoped 才能命中遮罩与 overlay-dialog -->
<style>
@media screen and (max-width: 600px) {
  .major-catalog-login-modal .el-overlay-dialog {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 12px 16px !important;
    box-sizing: border-box !important;
  }

  .major-catalog-login-modal .el-dialog.major-catalog-login-dialog {
    margin: auto !important;
    max-height: calc(100vh - 32px) !important;
    /* 避免依赖 100vw，与页面是否出现横向滚动条解耦 */
    width: min(400px, calc(100% - 32px)) !important;
    max-width: calc(100% - 32px) !important;
  }
}
</style>
